// MaturaLogPropClosedTask.js
// Zadanie maturalne zamknięte: log_a X ± log_a Y
// X i Y dobrane tak, aby dopiero iloczyn / iloraz był potęgą podstawy a^t.

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import ABCDInput from "../../../../components/inputs/ABCDInput";

/* ─────────── HELPERY ─────────── */

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
const powi = (a, k) => (k >= 0 ? Math.pow(a, k) : 1 / Math.pow(a, -k));

const logb = (a) => (a === 10 ? "\\log" : `\\log_{${a}}`);
const logExp = (a) => (a === 10 ? "\\log_{10}" : `\\log_{${a}}`);
const isIntegerOrProperFraction = (value) =>
  Number.isInteger(value) || (Math.abs(value) > 0 && Math.abs(value) < 1);

const gcd = (a, b) => {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b) {
    [a, b] = [b, a % b];
  }
  return a || 1;
};

function simplifyFrac(num, den) {
  if (den < 0) {
    num = -num;
    den = -den;
  }
  const g = gcd(num, den);
  return { num: num / g, den: den / g };
}

function toFraction(x, maxDen = 10000) {
  const sign = x < 0 ? -1 : 1;
  x = Math.abs(x);
  if (!isFinite(x)) return { num: sign, den: 0 };

  let h0 = 0,
    k0 = 1,
    h1 = 1,
    k1 = 0;
  let y = x;

  for (let i = 0; i < 20; i++) {
    const a = Math.floor(y);
    const h2 = a * h1 + h0;
    const k2 = a * k1 + k0;
    if (k2 > maxDen) break;
    h0 = h1;
    k0 = k1;
    h1 = h2;
    k1 = k2;
    const frac = y - a;
    if (frac < 1e-12) break;
    y = 1 / frac;
  }

  let num = h1,
    den = k1;
  if (k0 !== 0 && Math.abs(h0 / k0 - x) < Math.abs(num / den - x)) {
    num = h0;
    den = k0;
  }

  ({ num, den } = simplifyFrac(num * sign, den));
  return { num, den };
}

function niceLatexFromNumber(val) {
  if (!isFinite(val)) return String(val);

  const int = Math.round(val);
  if (Math.abs(val - int) < 1e-12) return String(int);

  let { num, den } = toFraction(val);
  ({ num, den } = simplifyFrac(num, den));

  const sgn = num < 0 ? "-" : "";
  num = Math.abs(num);

  if (den === 1) return `${sgn}${num}`;

  return `${sgn}\\frac{${num}}{${den}}`;
}

function isPowerOfBase(c, a) {
  for (let k = -6; k <= 6; k++) {
    if (Math.abs(c - Math.pow(a, k)) < 1e-10) return true;
  }
  return false;
}

const NICE_BY_BASE = {
  10: [2, 4, 5, 8, 25, 50, 1 / 2, 1 / 4, 4 / 5, 20, 1 / 5],
  5: [2, 4, 8, 10, 1 / 2, 1 / 4],
  4: [2, 8, 1 / 2, 10, 1 / 4, 2 / 5, 5],
  3: [2, 6, 1 / 2, 3 / 4, 12, 1 / 4],
  2: [3, 6, 3 / 4, 5, 10, 1 / 5, 2 / 5],
};

const MAX_RESULT_EXPONENT_BY_BASE = {
  2: 5,
  3: 4,
  4: 3,
  5: 3,
  10: 3,
};

export function getAllowedResultExponents(base) {
  const maxExponent = MAX_RESULT_EXPONENT_BY_BASE[base] ?? 3;
  return Array.from({ length: maxExponent - 1 }, (_, index) => index + 2)
    .flatMap((exponent) => [-exponent, exponent]);
}

/* ─────────── PARY DLA RÓŻNICY / SUMY ─────────── */

function buildDiffPair(a, t) {
  const Q = powi(a, t);
  const pool = NICE_BY_BASE[a] || NICE_BY_BASE[10];
  const candidates = pool.filter(
    (c) => isIntegerOrProperFraction(c) && isIntegerOrProperFraction(c * Q)
  );
  const c = pick(candidates.length ? candidates : [1]);
  const X = c * Q;
  const Y = c;
  return {
    X,
    Y,
    Xs: niceLatexFromNumber(X),
    Ys: niceLatexFromNumber(Y),
  };
}

function buildSumPair(a, t) {
  const P = powi(a, t);
  const pool = (NICE_BY_BASE[a] || NICE_BY_BASE[10]).filter(
    (c) => !isPowerOfBase(c, a)
  );

  const exps =
    t >= 0
      ? [...Array(t + 1).keys()] // 0..t
      : [...Array(-t + 1).keys()].map((i) => t + i); // t..0

  for (let tries = 0; tries < 40; tries++) {
    const u = pick(exps);
    const c = pick(pool);

    const X = c * powi(a, u);
    const Y = P / X;

    const nearIntX = Math.abs(X - Math.round(X)) < 1e-12;
    const nearIntY = Math.abs(Y - Math.round(Y)) < 1e-12;

    const xPure = nearIntX && isPowerOfBase(Math.round(X), a);
    const yPure = nearIntY && isPowerOfBase(Math.round(Y), a);

    if (
      !xPure &&
      !yPure &&
      isFinite(Y) &&
      isIntegerOrProperFraction(X) &&
      isIntegerOrProperFraction(Y)
    ) {
      return { X, Y, Xs: niceLatexFromNumber(X), Ys: niceLatexFromNumber(Y) };
    }
  }

  // fallback – prosty, ale poprawny przypadek
  const fallback = [2, 3, 5, 7, 1 / 2, 1 / 3]
    .flatMap((c) => exps.map((u) => ({ X: c * powi(a, u), c })))
    .find(({ X }) => isIntegerOrProperFraction(X) && isIntegerOrProperFraction(P / X));
  const X = fallback?.X ?? 1;
  const Y = P / X;
  return { X, Y, Xs: niceLatexFromNumber(X), Ys: niceLatexFromNumber(Y) };
}

/* ─────────── GENERATOR ─────────── */

export function buildLogPropertyData({a,t,op,X,Y,options}) {
  const Xs=niceLatexFromNumber(X),Ys=niceLatexFromNumber(Y);
  const exprLatex=`${logExp(a)} ${Xs} ${op} ${logExp(a)} ${Ys}`;
  return {a,t,op,X,Y,Xs,Ys,exprLatex,latex:exprLatex.replace(/ /g,""),options,correctIndex:options.findIndex(o=>o.isCorrect)};
}

/**
 * Wariant „oryginał” – matura podstawowa, sierpień 2025, zadanie 4:
 *   log_3 2 - log_3 18 = log_3 (1/9) = log_3 3^{-2}  →  A. (-2)
 */
function createOriginalTask() {
  const a = 3;
  const t = -2;
  const op = "-";
  const X = 2;
  const Y = 18;
  const Xs = "2";
  const Ys = "18";
  const exprLatex = "\\log_{3} 2 - \\log_{3} 18";

  const options = [
    { label: "A", latex: "-2", isCorrect: true },
    { label: "B", latex: "-\\frac{1}{2}", isCorrect: false },
    { label: "C", latex: "\\frac{1}{2}", isCorrect: false },
    { label: "D", latex: "2", isCorrect: false },
  ];
  const correctIndex = options.findIndex((o) => o.isCorrect);

  return {
    type: "MaturaLogPropClosedTask",
    data: { a, t, op, X, Y, Xs, Ys, exprLatex, options, correctIndex },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_SIE",
      taskNumber: 4,
    },
  };
}

/**
 * Wariant „oryginał” – matura podstawowa, maj 2026, zadanie 4:
 *   log_8 4 - log_8 32 = log_8 (1/8) = log_8 8^{-1}  →  B. (-1)
 */
function createMay2026OriginalTask() {
  const a = 8;
  const t = -1;
  const op = "-";
  const X = 4;
  const Y = 32;
  const Xs = "4";
  const Ys = "32";
  const exprLatex = "\\log_{8} 4 - \\log_{8} 32";

  const options = [
    { label: "A", latex: "-2", isCorrect: false },
    { label: "B", latex: "-1", isCorrect: true },
    { label: "C", latex: "1", isCorrect: false },
    { label: "D", latex: "2", isCorrect: false },
  ];
  const correctIndex = options.findIndex((option) => option.isCorrect);

  return {
    type: "MaturaLogPropClosedTask",
    data: {
      a, t, op, X, Y, Xs, Ys, exprLatex,
      latex: exprLatex.replace(/ /g, ""),
      options,
      correctIndex,
    },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2026_MAJ",
      taskNumber: "4",
    },
  };
}

export function generateTask(generationOptions = {}) {
  if(generationOptions.variant==="original"&&generationOptions.session==="2024_CZE"){
    const options=[{label:'A',latex:'\\log_{3}\\frac{31}{18}',isCorrect:false},{label:'B',latex:'\\log_{3}\\frac{5}{11}',isCorrect:false},{label:'C',latex:'-1',isCorrect:true},{label:'D',latex:'\\frac{1}{3}',isCorrect:false}];
    return {type:'MaturaLogPropClosedTask',data:buildLogPropertyData({a:3,t:-1,op:'+',X:3/2,Y:2/9,options}),source:{variant:'original',exam:'MATURA',session:'2024_CZE',taskNumber:'2'}};
  }
  if(generationOptions.variant==="original"&&generationOptions.session==="2023_CZE"){
    const options=["-1","\\frac12","2","5"].map((latex,i)=>({label:"ABCD"[i],latex,isCorrect:i===0}));
    return {type:"MaturaLogPropClosedTask",data:buildLogPropertyData({a:2,t:-1,op:"+",X:1/8,Y:4,options}),source:{variant:"original",exam:"MATURA",session:"2023_CZE",taskNumber:"5"}};
  }
  if(generationOptions.variant==="original"&&generationOptions.session==="2023_MAJ"){
    const options=["81","9","4","2"].map((latex,i)=>({label:"ABCD"[i],latex,isCorrect:i===3}));
    return {type:"MaturaLogPropClosedTask",data:buildLogPropertyData({a:9,t:2,op:"+",X:27,Y:3,options}),source:{variant:"original",exam:"MATURA",session:"2023_MAJ",taskNumber:"4"}};
  }
  if(generationOptions.variant==="original"&&generationOptions.session==="2023_GRU"){
    const options=["\\log_{2}93","\\log_{2}30","4","5"].map((latex,i)=>({label:"ABCD"[i],latex,isCorrect:i===3}));
    return {type:"MaturaLogPropClosedTask",data:buildLogPropertyData({a:2,t:5,op:"-",X:96,Y:3,options}),source:{variant:"original",exam:"MATURA",session:"2023_GRU",taskNumber:"2"}};
  }
  if (
    generationOptions.variant === "original" &&
    generationOptions.session === "2026_MAJ"
  ) {
    return createMay2026OriginalTask();
  }
  if (generationOptions.variant === "original") return createOriginalTask();

  const a = pick([2, 3, 4, 5, 10]);
  // t tak dobrane, żeby 1/t było ładną ułamkową (bez ±1)
  const t = pick(getAllowedResultExponents(a));
  const op = pick(["+", "-"]);

  const pair = op === "-" ? buildDiffPair(a, t) : buildSumPair(a, t);
  const { X, Y, Xs, Ys } = pair;

  const exprLatex =
    op === "-"
      ? `${logb(a)} ${Xs} - ${logb(a)} ${Ys}`
      : `${logb(a)} ${Xs} + ${logb(a)} ${Ys}`;

  // Cztery odpowiedzi: t, -t, 1/t, -1/t (losowa kolejność)
  const candidates = [t, -t, 1 / t, -1 / t];
  const LETTERS = ["A", "B", "C", "D"];

  const options = candidates
    .map((v) => ({
      value: v,
      latex: niceLatexFromNumber(v),
      isCorrect: v === t,
      sortKey: Math.random(),
    }))
    .sort((a, b) => a.sortKey - b.sortKey)
    .map((o, i) => ({
      label: LETTERS[i],
      latex: o.latex,
      isCorrect: o.isCorrect,
    }));

  const correctIndex = options.findIndex((o) => o.isCorrect);

  return {
    type: "MaturaLogPropClosedTask",
    data: {
      ...buildLogPropertyData({a,t,op,X,Y,options}),
      Xs,Ys,exprLatex,latex:exprLatex.replace(/ /g,""),correctIndex,
    },
    source: {
      variant: "random",
      basedOn: "MATURA_2025_SIE_4+MATURA_2026_MAJ_4",
    },
  };
}

/* ─────────── RENDER TASK ─────────── */

export function renderTask({ data }) {
  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Zadanie maturalne – zamknięte.</strong>
      </p>
      <p>
        Liczba <InlineMath math={data.exprLatex} /> jest równa:
      </p>

      <div style={{ marginTop: 8 }}>
        {data.options.map((opt, idx) => (
          <div key={idx} style={{ marginBottom: 4 }}>
            <InlineMath math={`${opt.label}.\\; ${opt.latex}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─────────── RENDER INPUT (ABCD) ─────────── */

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const choiceIndex =
    userAnswer && typeof userAnswer.choiceIndex === "number"
      ? userAnswer.choiceIndex
      : null;

  const handleChange = (idx) => {
    const opt = data.options[idx];
    onChange({
      choiceIndex: idx,
      label: opt.label,
      latex: opt.latex,
    });
  };

  const letterOptions = data.options.map((opt, idx) => ({
    value: idx,
    label: opt.label,
    textNode: opt.label,
  }));

  return (
    <ABCDInput
      options={letterOptions}
      value={choiceIndex}
      onChange={handleChange}
      disabled={disabled}
    />
  );
}

/* ─────────── CHECK ANSWER ─────────── */

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer.choiceIndex !== "number") return false;
  return userAnswer.choiceIndex === data.correctIndex;
}

/* ─────────── RENDER ANSWER (bez napisu) ─────────── */

export function renderAnswer({ data }) {
  const options = Array.isArray(data?.options) ? data.options : [];
  const correct = options[data?.correctIndex];
  if (!correct?.label || !correct?.latex) {
    return <i>(brak zapisanej odpowiedzi)</i>;
  }
  return <InlineMath math={`${correct.label}.\\; ${correct.latex}`} />;
}

/* ─────────── RENDER USER ANSWER ─────────── */

export function renderUserAnswer(userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer.choiceIndex !== "number" ||
    !userAnswer.label ||
    !userAnswer.latex
  ) {
    return <i>(brak odpowiedzi)</i>;
  }

  return <InlineMath math={`${userAnswer.label}.\\; ${userAnswer.latex}`} />;
}

/* ─────────── WYJAŚNIENIE ─────────── */

export function renderExplanation({ data }) {
  const { a, Xs, Ys, t, op } = data;

  const firstFormula =
    op === "-"
      ? "\\log_{a} x - \\log_{a} y = \\log_{a}\\left(\\tfrac{x}{y}\\right)"
      : "\\log_{a} x + \\log_{a} y = \\log_{a}(xy)";

  const combinedLatex =
    op === "-"
      ? `\\tfrac{${Xs}}{${Ys}}`
      : `${Xs}\\cdot${Ys}`;

  // nowy krok: najpierw wyliczamy wartość argumentu, dopiero potem pokazujemy go jako a^t
  const argValue = powi(a, t); // liczbowy argument logarytmu
  const argValueLatex = niceLatexFromNumber(argValue).replace(/\\frac/g, "\\tfrac");

  return (
    <div className="space-y-2" style={{ textAlign: "left" }}>
      <p>
        <strong>Krok 1.</strong> Korzystamy z własności logarytmów:
      </p>
      <BlockMath math={firstFormula} />
      {a === 10 && (
        <p style={{ marginTop: 6 }}>
          <em>Uwaga:</em>{" "}
          <InlineMath math={"\\log"} /> oznacza logarytm dziesiętny, czyli{" "}
          <InlineMath math={"\\log x = \\log_{10} x"} />.
        </p>
      )}

      <p>
        <strong>Krok 2.</strong> Podstawiamy liczby z zadania:
      </p>
      <BlockMath
        math={
          op === "-"
            ? `${logExp(a)} ${Xs} - ${logExp(a)} ${Ys} = ${logExp(
                a
              )}\\!\\left(${combinedLatex}\\right)`
            : `${logExp(a)} ${Xs} + ${logExp(a)} ${Ys} = ${logExp(
                a
              )}\\!\\left(${combinedLatex}\\right)`
        }
      />

      <p>
        <strong>Krok 3.</strong> Obliczamy argument logarytmu, a następnie
        zapisujemy go jako potęgę podstawy:
      </p>
      <BlockMath math={`${combinedLatex} = ${argValueLatex}`} />
      <BlockMath math={`${argValueLatex} = ${a}^{${t}}`} />

      <p>
        <strong>Krok 4.</strong> Z definicji logarytmu:
      </p>
      <BlockMath math={`${logExp(a)}(${a}^{${t}}) = ${t}`} />
    </div>
  );
}
